export type SupportedLocale = 'en' | 'ar';

const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en', 'ar'] as const;

const DEFAULT_LOCALE: SupportedLocale = 'en';

/**
 * Detects the current locale from URL path, query parameters, or default.
 *
 * Detection priority:
 * 1. URL path prefix (e.g., /ar/dashboard → 'ar')
 * 2. Query parameter (e.g., ?locale=ar → 'ar')
 * 3. `fallback` when the URL says nothing, defaulting to 'en'
 *
 * The fallback is a parameter because English is the platform default only for
 * the merchant apps. This app is Arabic-first and passes 'ar' rather than
 * rewriting the URL to fake a choice the user never made.
 */
export function getLocale(fallback: SupportedLocale = DEFAULT_LOCALE): SupportedLocale {
  try {
    const pathname = window.location.pathname;
    const pathSegments = pathname.split('/').filter((segment) => segment.length > 0);

    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0];
      if (isValidLocale(firstSegment)) {
        return firstSegment;
      }
    }

    const searchParams = new URLSearchParams(window.location.search);
    const localeParam = searchParams.get('locale');

    if (localeParam && isValidLocale(localeParam)) {
      return localeParam;
    }

    return fallback;
  } catch (error) {
    console.warn('Error detecting locale, falling back to default:', error);
    return fallback;
  }
}

export function isValidLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

export function getTextDirection(locale: SupportedLocale): 'ltr' | 'rtl' {
  return locale === 'ar' ? 'rtl' : 'ltr';
}

/**
 * Builds the URL that serves `targetLocale`, preserving the current path, query and hash.
 *
 * Deployed environments ship one AOT bundle per locale behind an `/en/` or `/ar/` path prefix, so
 * both locales are always prefixed. Local dev serves a single JIT bundle that picks its locale up
 * from the `locale` query param instead.
 */
export function buildLocaleSwitchUrl(
  targetLocale: SupportedLocale,
  isProduction: boolean,
  currentHref: string = window.location.href,
  appDefaultLocale: SupportedLocale = DEFAULT_LOCALE,
): string {
  const url = new URL(currentHref);

  if (isProduction) {
    url.pathname = `/${targetLocale}${url.pathname.replace(/^\/(en|ar)(?=\/|$)/, '')}`;
    return url.toString();
  }

  // Dropping the param is only safe when the target *is* what a bare URL
  // resolves to. For an app whose default is Arabic, deleting it on the way to
  // English sends the next load straight back to Arabic.
  if (targetLocale === appDefaultLocale) {
    url.searchParams.delete('locale');
  } else {
    url.searchParams.set('locale', targetLocale);
  }
  return url.toString();
}

export interface SwitchLocaleOptions {
  /** What a URL with no locale resolves to in this app. Defaults to 'en'. */
  appDefaultLocale?: SupportedLocale;
}

/** Reloads the document into `targetLocale`. Each locale is a separate build, so this is always a full navigation. */
export function switchLocale(
  targetLocale: SupportedLocale,
  isProduction: boolean,
  options: SwitchLocaleOptions = {},
): void {
  const { appDefaultLocale = DEFAULT_LOCALE } = options;
  // `replace`, not assign: the page we are leaving is the same page in the old language, so
  // keeping it on the stack makes the next back press look like it did nothing.
  window.location.replace(
    buildLocaleSwitchUrl(targetLocale, isProduction, window.location.href, appDefaultLocale),
  );
}
